import { Preferences } from '@capacitor/preferences';

import { isNativePlatform } from 'services/auth/platform';

/**
 * Runtime tenant (instance) configuration for the native (Capacitor) shell.
 *
 * The web build bakes every tenant value into the bundle at build time via `import.meta.env`,
 * but a single native binary must connect to any customer instance chosen at runtime.
 * This store mirrors `tokenStore.ts`: an in-memory cache for synchronous reads, hydrated once from Preferences
 * at boot and written through on every change so the selection survives relaunches.
 *
 * On web the cache is seeded synchronously from `import.meta.env` at module load,
 * so every getter returns the build-time value and web behaviour is unchanged.
 * On native the cache is null until a workspace is selected (deep link or manual entry).
 */

export interface RuntimeTenantConfig {
  schemaVersion: number;
  host: string;
  apiBaseUrl: string;
  webAppBaseUrl: string;
  authMethods: string;
  google: { webClientId: string; iosClientId: string };
  microsoft: { clientId: string; tenantId: string };
  sentryDsn?: string;
  sentryEnvironment?: string;
  stripePublishableKey?: string;
}

export const RUNTIME_CONFIG_SCHEMA_VERSION = 1;

const RUNTIME_CONFIG_KEY = 'a1_runtime_config';

const buildWebConfig = (): RuntimeTenantConfig => ({
  schemaVersion: RUNTIME_CONFIG_SCHEMA_VERSION,
  host: import.meta.env.VITE_APP_DOMAIN ?? '',
  apiBaseUrl: import.meta.env.VITE_APP_AZ_FUNCTIONS_BASE_URI ?? '',
  webAppBaseUrl: import.meta.env.VITE_APP_WEB_APP_BASE_URL ?? '',
  authMethods: import.meta.env.VITE_APP_AUTH_METHODS ?? 'microsoft,google,password',
  google: {
    webClientId: import.meta.env.VITE_APP_AUTH_GOOGLE_CLIENT_ID ?? '',
    iosClientId: import.meta.env.VITE_APP_AUTH_GOOGLE_CLIENT_ID_IOS ?? ''
  },
  microsoft: {
    clientId: import.meta.env.VITE_APP_AUTH_MICROSOFT_CLIENT_ID ?? '',
    tenantId: import.meta.env.VITE_APP_AUTH_MICROSOFT_TENANT_ID ?? 'common'
  },
  sentryDsn: import.meta.env.VITE_SENTRY_DSN,
  sentryEnvironment: import.meta.env.VITE_APP_ENV ?? 'local',
  stripePublishableKey: import.meta.env.VITE_APP_STRIPE_PUBLISHABLE_KEY
});

// Web is always configured from the build-time env; native starts unconfigured.
let config: RuntimeTenantConfig | null = isNativePlatform() ? null : buildWebConfig();

export const getConfig = (): RuntimeTenantConfig | null => config;

export const isConfigured = (): boolean => config != null;

export const getApiBaseUrl = (): string => config?.apiBaseUrl ?? '';

export const getWebAppBaseUrl = (): string => config?.webAppBaseUrl ?? '';

export const getAuthMethods = (): string => config?.authMethods ?? '';

export const getGoogleConfig = (): RuntimeTenantConfig['google'] => config?.google ?? { webClientId: '', iosClientId: '' };

export const getMicrosoftConfig = (): RuntimeTenantConfig['microsoft'] => config?.microsoft ?? { clientId: '', tenantId: 'common' };

export const getSentryDsn = (): string | undefined => config?.sentryDsn;

export const getSentryEnvironment = (): string | undefined => config?.sentryEnvironment;

export const getStripePublishableKey = (): string | undefined => config?.stripePublishableKey;

/** Host segment for device-local storage keys. */
export const getStorageScopeHost = (): string | null => (isNativePlatform() ? (config?.host ?? null) : null);

/** Stores the selected tenant config in memory and persists it to Preferences. Native only. */
export const setConfig = async (newConfig: RuntimeTenantConfig): Promise<void> => {
  config = newConfig;
  await Preferences.set({ key: RUNTIME_CONFIG_KEY, value: JSON.stringify(newConfig) });
};

/** Clears the selected tenant config from memory and Preferences. Native only. */
export const clear = async (): Promise<void> => {
  config = null;
  await Preferences.remove({ key: RUNTIME_CONFIG_KEY });
};

/** Loads the persisted tenant config from Preferences into memory. Call once at app boot before render. */
export const hydrate = async (): Promise<void> => {
  const stored = await Preferences.get({ key: RUNTIME_CONFIG_KEY });

  if (!stored.value) return;

  try {
    const parsed = JSON.parse(stored.value) as RuntimeTenantConfig;
    if (parsed.schemaVersion === RUNTIME_CONFIG_SCHEMA_VERSION) {
      config = parsed;
    }
  } catch (error) {
    // Corrupt or stale config leaves it unconfigured so the user can re-select the instance.
    console.error('Failed to parse the persisted runtime config; the instance will need re-selecting.', error);
  }
};
